import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { type ChangeEvent, useRef } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CacheKeys } from '@/lib/cache'
import { AD_HOC_ACCEPTED_FILE_TYPES } from '@/lib/fileUpload'
import { DocumentType, uploadOrgDocument } from '@/models/document'

/**
 * Uploads whatever the customer wants us to have as "Other". Nothing is parsed off
 * these — they file against the org, so a document that looks like a driver list
 * still leaves the fleet alone. Typed uploads (loss runs, imports) have their own cards.
 */
export function UploadOtherDocumentsButton({ orgId }: { orgId: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const upload = useMutation({
    // allSettled: `all` settles on the first rejection, so retrying re-uploads whatever already landed.
    mutationFn: async (files: File[]) => {
      const results = await Promise.allSettled(
        files.map((file) => uploadOrgDocument(orgId, file, DocumentType.OTHER))
      )
      return results.filter((result) => result.status === 'rejected').length
    },
    onSuccess: (failed, files) => {
      const uploaded = files.length - failed
      if (uploaded > 0) {
        toast.success(uploaded === 1 ? 'Document uploaded' : `${uploaded} documents uploaded`)
      }
      if (failed > 0) {
        toast.error(
          failed === 1
            ? "One file didn't go through. Please try it again."
            : `${failed} files didn't go through. Please try them again.`
        )
      }
    },
    onError: () => toast.error("That upload didn't go through. Please try again."),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORG_DOCUMENTS, orgId] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORG_DOCUMENT_GROUPS, orgId] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORG_DOCUMENTS_BY_TYPE, orgId] })
    },
  })

  function onFilesChosen(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    // Clear first so picking the same file twice still fires a change event.
    event.target.value = ''
    if (files.length > 0) upload.mutate(files)
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={AD_HOC_ACCEPTED_FILE_TYPES}
        className="hidden"
        onChange={onFilesChosen}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={upload.isPending ? 'Uploading…' : undefined}
        onClick={() => inputRef.current?.click()}
      >
        <Upload /> Upload document
      </Button>
    </>
  )
}
